/**
 * usePermission — verificação de permissão por flag do perfil ativo.
 *
 * Combina dois dados:
 *   1. /me           → para descobrir qual `profile_id` o usuário tem no
 *                      tenant atualmente ativo (vem em `accessible_tenants`).
 *   2. /profiles     → para obter as flags do perfil identificado em (1).
 *
 * Ambas as queries são cacheadas pelo TanStack Query e tipicamente já foram
 * pré-carregadas no bootstrap (useInitializeApp) ou pelo TenantSwitcher,
 * então o overhead em runtime é negligível.
 *
 * Retorna **`false`** em qualquer condição "ainda não dá pra saber":
 *   • Sem tenant ativo (admin global, login pendente)
 *   • /me ou /profiles em loading / erro
 *   • Perfil do usuário não encontrado na lista (race após troca de tenant)
 *   • Flag inexistente no perfil
 *
 * Isso garante UX **fail-closed**: a UI esconde a ação até confirmar
 * positivamente a permissão. Crítico para botões destrutivos.
 *
 * Spec §6 ("Verificação de permissão"), §18 ("Renderização Baseada em
 * Permissões") — define o hook como ponto único de truth para gates.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'

import { queryKeys } from '@/lib/query-keys'
import { authService } from '@/services/auth.service'
import { profilesService } from '@/services/profiles.service'
import { useTenantStore } from '@/store/tenant.store'
import type { MeResponse, Profile } from '@/types/api'

/** Mesma janela de visita do Orchestrator (`access_audit.VISIT_WINDOW`). */
export const ME_STALE_MS = 30 * 60 * 1000

/**
 * Flags booleanas do perfil que controlam permissões.
 *
 * Extraída via `keyof Profile` filtrando para apenas as propriedades
 * booleanas. `name`, `id` e `is_system_default` são excluídos: não fazem
 * sentido como gate.
 */
export type PermissionFlag =
  | 'can_create_test'
  | 'can_view_tests'
  | 'can_edit_test'
  | 'can_delete_test'
  | 'can_execute_test'
  | 'can_manage_schedules'
  | 'can_view_reports'
  | 'can_manage_users'
  | 'can_view_billing'
  | 'can_approve_curation'

export interface PermissionStatus {
  hasPermission: boolean
  isLoading: boolean
}

/**
 * Verifica se o usuário tem a permissão `flag` no contexto do tenant ativo.
 *
 * @param flag Nome da flag booleana do perfil (ex: `'can_execute_test'`).
 * @returns `true` se a flag está habilitada no perfil do tenant ativo;
 *          `false` em todos os outros casos (loading, erro, sem tenant,
 *          flag ausente).
 *
 * @example
 *   const canExecute = usePermission('can_execute_test')
 *   {canExecute && <Button onClick={handleExecute}>Executar</Button>}
 */
export function usePermissionStatus(flag: PermissionFlag): PermissionStatus {
  const activeTenant = useTenantStore((s) => s.activeTenant)
  const activeTenantId = activeTenant?.id ?? null

  // /me — globalmente cacheado (não tenant-escopado); o cache é limpo via
  // queryClient.clear() em useAuth.logout. O GET /me também é o sinal de
  // VISITA da auditoria de acesso: ao voltar o foco da aba depois de
  // ME_STALE_MS, o refetch avisa o servidor, que deduplica na mesma janela.
  // O refetch não pisca as permissões (isLoading olha `isPending`).
  const meQuery = useQuery<MeResponse>({
    queryKey: queryKeys.me(),
    queryFn: () => authService.getMe(),
    enabled: activeTenantId !== null,
    staleTime: ME_STALE_MS,
    refetchOnWindowFocus: true,
  })

  // /profiles — escopado pelo tenant ativo. Reexecutado automaticamente
  // quando `activeTenantId` muda (queryKey é diferente).
  const profilesQuery = useQuery<Profile[]>({
    queryKey: activeTenantId ? queryKeys.profiles(activeTenantId) : ['profiles', 'inactive'],
    queryFn: () => profilesService.list(),
    enabled: activeTenantId !== null,
  })

  return useMemo(() => {
    const isLoading =
      activeTenantId !== null && (meQuery.isPending || profilesQuery.isPending)
    if (!activeTenantId) return { hasPermission: false, isLoading: false }
    const me = meQuery.data
    const profiles = profilesQuery.data
    if (!me || !profiles) return { hasPermission: false, isLoading }

    // Descobre o profile_id que o usuário tem neste tenant.
    const access = me.accessible_tenants.find((t) => t.tenant_id === activeTenantId)
    if (!access) return { hasPermission: false, isLoading }

    // Resolve o perfil completo (com as flags) na lista de perfis do tenant.
    const profile = profiles.find((p) => p.id === access.profile_id)
    if (!profile) return { hasPermission: false, isLoading }

    // Acessa a flag — o cast é seguro porque `PermissionFlag` é um subset
    // estrito de `keyof Profile` restrito a propriedades booleanas.
    const value = (profile as unknown as Record<string, unknown>)[flag]
    return { hasPermission: value === true, isLoading }
  }, [
    activeTenantId,
    flag,
    meQuery.data,
    meQuery.isPending,
    profilesQuery.data,
    profilesQuery.isPending,
  ])
}

export function usePermission(flag: PermissionFlag): boolean {
  return usePermissionStatus(flag).hasPermission
}
